import { Ext } from "../../Doc";
import type { GuideProps } from "../Guide";

/**
 * The member guide, Arabic translation of en.tsx. Every number comes from `limits`; every claim
 * about who can read what follows the threat model (PLAN.md 4.12). Keep it in step with en.tsx.
 */
export function GuideAr({ limits: l, link }: GuideProps) {
  return (
    <>
      <h1>كيف يعمل Vrelis</h1>
      <p className="lead">
        Vrelis خدمة لإرسال رسائل مشفّرة، هي الكبسولات الزمنية، لا يمكن قراءتها إلا بدءًا من لحظة تختارها أنت: من جميع المشاركين في التجربة، أو من
        شخص واحد. قبل تلك اللحظة لا يستطيع أحد قراءة الكبسولة: لا الشخص الذي كتبت إليه، ولا من يديرون Vrelis، ولا أنت.
      </p>
      <p className="warn">
        هذه تجربة مغلقة. نزّل الكبسولات التي تريد الاحتفاظ بها (انظر أدناه)، واحتفظ بعبارة الاسترداد.
      </p>

      <h2>لماذا يمكن الوثوق بالقفل</h2>
      <p>
        القفل ليس وعدًا من Vrelis. إنه تشفير منشور علنًا مع إشارة زمنية عامة لا يتحكم فيها Vrelis. إليك ما يجعله صامدًا،
        وما يجب أن تصدّقه حتى يفشل.
      </p>

      <h3>drand: ساعة عامة توقّع الوقت</h3>
      <ul>
        <li>
          <Ext href="https://drand.love"><b>drand</b></Ext> شبكة تديرها{" "}
          <Ext href="https://www.cloudflare.com/leagueofentropy/">League of Entropy</Ext>، وهي مجموعة من المؤسسات المستقلة (منها Cloudflare وProtocol Labs وKudelski
          Security وEPFL وUniversity College London) تنشر معًا قيمة عشوائية موقّعة كل 3 ثوانٍ. القيم مرقّمة: الجولة 1،
          والجولة 2، وهكذا، ولكل منها لحظة معروفة. يستطيع أي شخص قراءتها من عدة مرحّلات عامة؛ ولا يستطيع أحد قراءة جولة مستقبلية، لأنها لا
          توجد بعد.
        </li>
        <li>
          تُوقَّع كل جولة بتشفير العتبة (<Ext href="https://en.wikipedia.org/wiki/BLS_digital_signature">تواقيع BLS</Ext>): تحمل كل مؤسسة حصة واحدة من مفتاح سري، ويحتاج التوقيع إلى
          أغلبية الحصص. لا تستطيع أي مؤسسة منفردة، ولا أقلية منها، إنتاج توقيع جولة قبل وقتها. تعمل الشبكة
          منذ 2020؛ والسلسلة التي نستخدمها،{" "}
          <Ext href="https://api.drand.sh/v2/chains/52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971/info">quicknet</Ext>، منذ 2023.
          مفتاحها العام مثبّت داخل الكود لدينا، وكل إشارة نتلقاها تُفحص مقابله.
        </li>
        <li>
          <b>التشفير بالقفل الزمني</b> (<Ext href="https://eprint.iacr.org/2023/189">tlock</Ext>، الذي نشره باحثون من Protocol Labs وCloudflare
          في 2023) يحوّل تلك الساعة إلى قفل: تُشفَّر الرسالة
          لرقم جولة، وتوقيع الجولة، حين يُنشر، هو المفتاح الوحيد الذي يفتحها. وإلى أن ينشره drand، لا يوجد ذلك المفتاح
          في أي مكان في العالم.
        </li>
      </ul>

      <h3>كيف يستخدمه Vrelis</h3>
      <ul>
        <li>
          يشفّر متصفحك النص ومفاتيح ملفاتك لجولة drand الموافقة للوقت الذي اخترته؛ وتعرض صفحة الكتابة رقم الجولة قبل الإرسال.
          وتُشفَّر الكبسولة الخاصة مرة ثانية لمفتاح المستلم، فلا يستطيع فتحها بعد الوقت إلا هو.
        </li>
        <li>
          لا يتلقى خادمنا إلا النتيجة المختومة ويخزّنها. لا يحمل أي مفتاح يفتح كبسولة: لا قبل الوقت، ولا بعده أيضًا
          في حالة الكبسولة الخاصة. أي تسريب لقاعدة البيانات سيكشف نصًا مشفّرًا وبيانات وصفية، ولن يكشف رسالة أبدًا.
        </li>
        <li>
          عندما يحين الوقت، يجلب متصفحك (أو أداة الفتح دون اتصال) توقيع الجولة من أحد أربعة مرحّلات drand عامة، ويتحقق منه
          مقابل المفتاح العام المثبّت، ثم يفك التشفير. لا دور لـ Vrelis في تلك الخطوة.
        </li>
        <li>
          كل كبسولة موقّعة بمفتاح كاتبها (<Ext href="https://en.wikipedia.org/wiki/Secp256k1">secp256k1</Ext>، وهو نوع المفتاح نفسه الذي يستخدمه Bitcoin
          و<Ext href="https://nostr.com">Nostr</Ext>)، فيُكتشف أي تغيير في الكبسولة. يُنشأ المفتاح في متصفحك من كلماتك الـ 12 (
          <Ext href="https://github.com/bitcoin/bips/blob/master/bip-0039.mediawiki">BIP-39</Ext>) ولا يغادره أبدًا دون تشفير.
        </li>
      </ul>

      <h3>إذا اختفى Vrelis</h3>
      <ul>
        <li>
          الصيغ مفتوحة: الكبسولة <Ext href="https://github.com/nostr-protocol/nips/blob/master/01.md">حدث Nostr</Ext> (وفي حالة الكبسولة الخاصة{" "}
          <Ext href="https://github.com/nostr-protocol/nips/blob/master/59.md">تغليف هدية NIP-59</Ext>) محتواه ملف{" "}
          <Ext href="https://age-encryption.org">age</Ext> مع مقطع <Ext href="https://github.com/drand/tlock-js">tlock</Ext>. أي برنامج
          يطبّق هذه المواصفات المفتوحة يستطيع فتح كبسولاتك؛ وبرنامجنا ليس ضروريًا.
        </li>
        <li>
          <b>التنزيل</b> يحفظ الكبسولات على قرصك أنت، مع أداة الفتح دون اتصال: ملف HTML واحد يفتحها على حاسوبك، دون أي
          خادم لـ Vrelis، باستخدام إشارة drand المطلوبة، ومفتاحك في حالة الكبسولة الخاصة. يمكن فتح التصدير الكامل دون vrelis.com إذا كانت
          الإشارة المطلوبة متاحة واحتفظت بالملفات والمفاتيح التي يحتاجها.
        </li>
        <li>
          إذا توقف drand نفسه يومًا قبل إنتاج الإشارة المستقبلية المطلوبة، فقد تبقى تلك الكبسولات مقفلة إلى الأبد. تنزيلها
          مسبقًا لا يزيل هذا الخطر. والكبسولة التي مضى تاريخ فتحها لا تزال تحتاج إلى إشارتها الدقيقة المُتحقق منها: من دون تلك الإشارة في
          أرشيفك أو متاحةً من أحد المرحّلات، لا يتيح لك التاريخ وحده فتحها دون اتصال. احتفظ بتصديرات كاملة وبالمفاتيح التي تتطلبها.
        </li>
      </ul>

      <h3>ما يجب أن تصدّقه حتى يفشل</h3>
      <ul>
        <li>
          أن تتواطأ أغلبية مؤسسات drand لتوقيع جولة مبكرًا. إنها مستقلة، في عدة بلدان، والسلسلة عامة، لذا
          سيكون مثل هذا التوقيع مرئيًا للجميع. هذا هو افتراض الثقة الذي يقوم عليه النظام كله.
        </li>
        <li>
          أن تكذب صفحة الويب. الصفحة كود يقدّمه Vrelis إلى متصفحك؛ ومن يتحكم في ذلك الكود يستطيع قراءة النص قبل ختمه، أو
          مفتاحك بعد أن تفتح قفله. لهذا يمكن تنزيل الكبسولات وفتحها دون اتصال، ولهذا تُعرض بصمات المفاتيح لتقارنها خارج
          Vrelis، ولهذا يُخطَّط بعد التجربة لتطبيق هاتف يُبقي المفتاح خارج صفحة الويب.
        </li>
        <li>
          أن يُكسر تشفير اليوم. تشفير المنحنيات الإهليلجية، الذي يستخدمه drand ومفتاحك كلاهما، قد تكسره في المستقبل{" "}
          <Ext href="https://en.wikipedia.org/wiki/Post-quantum_cryptography">الحواسيب الكمومية</Ext>.
          بالنسبة إلى الكبسولات التي يُراد لها أن تبقى مختومة لعقود، هذا خطر حقيقي لا تحله التجربة.
        </li>
      </ul>

      <h2>الدخول</h2>
      <ol>
        <li>
          إذا اعتمد المسؤول عنوانك، فسجّل به مباشرة؛ وإلا فافتح رابط دعوتك واستخدم عنوانها. سجّل الدخول بحساب Google ذلك (لا يشارك Google مع Vrelis سوى
          اسمك وبريدك الإلكتروني)، أو بكلمة مرور (نرسل رابطًا بالبريد لتأكيد العنوان). يمكنك لاحقًا إضافة مفتاح مرور لتسجيل الدخول.
        </li>
        <li>
          يعرض Vrelis إنشاء <b>مفتاحك</b> في متصفحك: توقيعك الأساسي، الذي يوقّع ما ترسله ويفتح الكبسولات الخاصة المرسلة إليك.
          يمكنك تخطيه وإنشاؤه لاحقًا في الحساب، المفاتيح؛ ومن دون مفتاح لا تقرأ إلا الكبسولات العامة. قبل أن تظهر <b>عبارة الاسترداد</b> (12 كلمة
          تعيد بناء المفتاح على أي جهاز)، يُطلب منك التأكد من أن أحدًا لا يستطيع رؤية شاشتك أو تسجيلها.
        </li>
        <li>اكتب الكلمات الـ 12 على ورقة. يُطلب منك كتابة ثلاث منها مجددًا، ثم تسجيل الدخول مرة أخرى لتأكيد أنك أنت: بكلمة المرور أو بمفتاح المرور، أو عبر Google لمفتاحك الأول تحديدًا.</li>
        <li>
          اختر رمز PIN (من {l.pinDigits} أرقام على الأقل) وأنشئ <Ext href="https://passkeys.dev/docs/intro/what-are-passkeys/">مفتاح مرور</Ext> (بصمة الإصبع،
          أو الوجه، أو رمز PIN لجهازك). معًا يحميان المفتاح على هذا الجهاز
          والنسخة التي يحتفظ بها Vrelis للانتقال إلى جهاز جديد. إذا لم يستطع متصفحك استخدام مفتاح مرور بهذه الطريقة، يبقى المفتاح دون حماية في ذلك المتصفح
          ولا توجد نسخة على الخادم؛ وتصبح العبارة حينها الطريق الوحيد إلى جهاز آخر.
        </li>
      </ol>

      <h2>عبارة الاسترداد</h2>
      <ul>
        <li>احتفظ بها على ورقة، في مكان آمن. لا تصوّرها ولا تخزّنها على حاسوب أو في مدير كلمات المرور.</li>
        <li>
          من يملك الكلمات الـ 12 <b>هو</b> أنت: يستطيع التوقيع باسمك وفتح كبسولاتك الخاصة. إذا تسرّبت، فاستخدم <b>إعادة تعيين المفتاح</b> في الحساب،
          المفاتيح: تكتب العبارة، وتسجّل الدخول مجددًا، ويُلغى المفتاح نهائيًا؛ فلا يستطيع أحد الإرسال به أو إليه بعد ذلك. تذكر منذ متى ربما
          سُرق، فتُوسم الكبسولات المرسلة به منذ ذلك الحين بـ «مفتاح مُلغى». ثم تنشئ مفتاحًا جديدًا بعبارة جديدة.
        </li>
        <li>
          يمكن أن تدخل مفاتيحك السابقة في <b>حلقة المفاتيح</b>، مشفّرة بمفتاحك الحالي، لتظل الكبسولات المرسلة إلى مفتاح قديم تُفتح دون العبارة القديمة.
          ويمكنك أيضًا الاحتفاظ بالعبارة القديمة بنفسك وإضافتها إلى حلقة المفاتيح لاحقًا.
        </li>
        <li>
          إذا فقدت جهازًا: سجّل الدخول على جهاز آخر واستخدم مفتاح المرور ورمز PIN (إن كان مفتاح المرور متزامنًا) أو عبارتك. وإذا نسيت رمز PIN: استخدم
          العبارة. بعد {l.pinAttempts} رموز PIN خاطئة تُحظر النسخة على الخادم؛ وتكتب العبارة نسخة جديدة.
        </li>
        <li>
          إذا فقدت العبارة <b>و</b>كل جهاز عليه مفتاحك، فلن يمكن فتح كبسولاتك الخاصة أبدًا، من قبل أي أحد. لا يستطيع المسؤول إلا منحك
          هوية جديدة. إلغاء المفتاح يتطلب العبارة أيضًا، لذا يلغيه المسؤول نيابة عنك إن لم تكن لديك.
        </li>
      </ul>

      <h2>كتابة كبسولة</h2>
      <ul>
        <li>
          <b>جميع المشاركين في التجربة</b> (عامة) أو <b>عضو واحد</b> (خاصة). يستطيع كل عضو قراءة الكبسولة العامة بعد وقتها؛ أما الخاصة فلا يقرؤها
          إلا الشخص الذي كتبت إليه، وأنت إن احتفظت بنسخة (مفعّلة افتراضيًا).
        </li>
        <li>
          تحتاج كل كبسولة إلى <b>موضوع</b>. لا يُشفَّر أبدًا: يقرؤه فورًا كل من يرى الكبسولة، وكذلك خادمنا.
        </li>
        <li>
          <b>المقدمة</b> اختيارية: حتى {l.introChars} حرفًا يمكن قراءتها قبل الوقت، عند النقر على الكبسولة. في الكبسولة العامة
          يقرؤها الجميع، وخادمنا أيضًا. وفي الخاصة لا يقرؤها إلا المستلم، بمفتاحه؛ ولا يراها الخادم. تبقى الرسالة
          وملفاتها مقفلة في الحالتين.
        </li>
        <li>
          هوية مرسل الكبسولة الخاصة مختومة لمفتاح المستلم: يراها بمجرد أن يفتح الكبسولة بمفتاحه، قبل وقتها بكثير.
          ومن يملك الكبسولة دون ذلك المفتاح لا يرى إلا الموضوع والوقت. يعرف خادمنا أي حساب أرسلها. ولا يخبر بذلك إلا المستلم، مشفّرًا لمفتاحه، ليتمكن التطبيق من التحقق من أن المفتاح الذي وقّع الكبسولة يخص ذلك الحساب؛ وإلا فإنه يعرض «مرسل غير موثّق» بدلًا من الاسم.
        </li>
        <li>
          <b>إخفاء المرسل</b> (الكبسولات الخاصة): تُظهر قوائم المستلم «مرسل مخفي» بدلًا من اسمك حتى ينقر على الكبسولة، فلا يرى من
          ينظر من فوق كتفه من يكتب إليه. ويظل المستلم قادرًا على رؤية أنك المرسل، قبل الوقت.
        </li>
        <li>
          اختر متى تُفتح: بعد 5 دقائق، أو ساعة، أو يوم، أو أسبوع، أو سنة، أو في تاريخ حتى عام 2125. يُعرض وقت الفتح الحقيقي قبل الإرسال؛ وهو
          دقيق إلى حدود بضع ثوانٍ.
        </li>
        <li>
          النص: حتى {l.publicText} للكبسولة العامة، و{l.privateText} للخاصة. الملفات: حتى {l.files} ملفات، وبإجمالي {l.fileBytes} في الخطة المجانية أو {l.planFileBytes} مع الخطة القديمة أو Start. تُصغَّر
          الصور وتُحذف بياناتها الوصفية، بما فيها الموقع.
        </li>
        <li>
          يطلب <b>ختم وإرسال</b> رمز PIN ومفتاح المرور مرة واحدة، لتوقيع الكبسولة. بعد ذلك لا يستطيع أحد تغييرها، ولا يستطيع أحد قراءتها قبل
          وقتها.
        </li>
        <li>
          قبل أن تكتب إلى شخص كتابة خاصة، تحقق من مفتاحه: تعرض صفحة الكتابة بصمته (16 حرفًا). إذا تغيّر مفتاحه منذ آخر مرة
          رآه فيها هذا المتصفح، تحذّرك صفحة الكتابة وتنتظر حتى تتأكد معه، خارج Vrelis، أن المفتاح الجديد له فعلًا.
        </li>
      </ul>

      <h2>القراءة</h2>
      <ul>
        <li>
          <b>الخلاصة</b>: الكبسولات العامة. <b>الوارد</b>: الكبسولات الخاصة الموجهة إليك. <b>المرسلة</b>: كبسولاتك. <b>قيد المتابعة</b>: كبسولات عامة تنتظرها. تابع
          كاتبًا لترى كبسولاته وحدها.
        </li>
        <li>
          <b>أخفِ</b> عضوًا: تغادر كبسولاته العامة الخلاصة لديك، على كل جهاز؛ ولا يتغير شيء بالنسبة إليه. <b>احظر</b> عضوًا: لا يعود
          قادرًا على وضع كبسولات خاصة في الوارد لديك (تخبره صفحة الكتابة بذلك قبل الإرسال)؛ ويمكنك مع ذلك متابعته وقراءة كبسولاته العامة.
          تنتقل كبسولاته السابقة إلى الوارد، المحظورون، وتظل تُفتح. كلا الخيارين في قائمة الشخص وفي صفحة الأشخاص، مع من يتابعك.
          لا يضمن Vrelis إلا ما يتلقاه ويسلّمه: لا يستطيع أحد منع شخص من التشفير لمفتاحك العام خارج Vrelis.
        </li>
        <li>
          لا تُظهر الكبسولة الخاصة شيئًا حتى تفتحها: يطلب <b>فتح</b> رمز PIN، ثم يعرض من أرسلها ومتى تُفتح، والنص حين يحين ذلك
          الوقت.
        </li>
        <li>
          يبقى مفتاحك مقفلًا؛ وكل إجراء يحتاج إليه يطلب رمز PIN. لقراءة عدة كبسولات متتالية، استخدم <b>فتح القفل لمدة … دقيقة</b> في الشريط
          أعلى الصفحة. <b>قفل</b> يخفي كل ما هو خاص دفعة واحدة؛ وكذلك مغادرة الصفحة.
        </li>
        <li>
          في <a href="/account">الحساب</a>، الخصوصية: «إظهار المرسلين في قوائمي فقط بعد أن أنقر على الكبسولة» يعامل كل كبسولة خاصة كأنها بمرسل مخفي،
          على كل جهاز.
        </li>
        <li>
          تعرض القوائم 50 كبسولة في كل مرة؛ ويضيف <b>عرض المزيد</b> الكبسولات التالية. يصفّيها Vrelis ويرتّبها (حسب وقت الفتح أو وقت
          استلامه لها)، لذا يرسل عامل التصفية أو المجموعة إلى Vrelis معرّفات الأعضاء الذين اخترتهم، لا المجموعة ولا اسمها أبدًا. تصفية الوارد حسب المرسل تتطلب
          مفتاحك، وتستثني الكبسولات ذات المرسل المخفي، وتتوقف ما دام المرسلون لا يظهرون إلا بعد النقر.
        </li>
        <li>
          <b>البحث</b> يبحث في المواضيع، ومقدمات الكبسولات العامة، ومن كتب كبسولة عامة، ومن أرسلت إليهم. لا يبحث أبدًا في النص المفتوح
          (لا يملكه إلا جهازك) ولا فيمن أرسل إليك كبسولة خاصة.
        </li>
      </ul>

      <h2>ظرف الرد وCC والمراسلات</h2>
      <ul>
        <li>
          يمكن أن تحمل الرسالة الخاصة <b>ظرف رد</b>: تطلب من كل مستلم ردًا خاصًا واحدًا إليك، مختومًا لمفتاحك.
          يستطيع الرد حتى إن لم يكن يتابعك. تختار إن كان كل رد يُفتح في وقت تحدده أنت (وبعد ذلك الوقت لا يُقبل أي
          رد) أو في وقت يختاره المستلم. تدفع ثمن الكبسولة والرد مقدمًا: طابعان لكل مستلم بدلًا من طابع واحد. الرد الأول للمستلم لا يكلّفه شيئًا؛ أما الثاني فيكلّفه طابعًا، وترى كليهما.
        </li>
        <li>
          <b>CC</b>: يمكن أن تذهب الرسالة الخاصة الموجهة إلى عضو واحد إلى أعضاء آخرين في الوقت نفسه، ويرى كل من في القائمة من تلقاها أيضًا. القائمة
          مختومة داخل الرسالة مع توقيع المرسل؛ ولا يراها خادمنا أبدًا. أما الإرسال الجماعي دون CC فيبقى مخفيًا: لا يرى أحد
          من تلقاها غيره.
        </li>
        <li>
          تعيش الرسائل المرفقة بظرف رد والردود عليها في صفحة <b>المراسلات</b>، لا في الوارد أو المرسلة: تحت كل
          طلب ترى ردود كل مستلم بالترتيب، مقفلة حتى وقتها، ومن دفع ثمن كل منها. يذهب الرد إلى المرسل وحده.
        </li>
      </ul>

      <h2>الاحتفاظ بالكبسولات دون Vrelis</h2>
      <ul>
        <li>
          <b>تنزيل</b> على الكبسولة يحفظها كملف؛ ومع المرفقات يكون ملف ‎.zip يضم الملفات وأداة الفتح دون اتصال. حدّد عدة كبسولات واستخدم{" "}
          <b>تنزيل المحدد</b> للحصول على أرشيف واحد («تحديد كل المعروض» يأخذ الكبسولات المعروضة).
        </li>
        <li>
          أداة الفتح دون اتصال ملف HTML واحد (<a href="/account">الحساب</a>، أو داخل ملف ‎.zip). تفتح كبسولاتك على حاسوبك أنت، والخاصة منها بعبارتك. لا
          تتصل أبدًا بـ vrelis.com؛ ولا تطلب من خدمة الوقت العامة drand إلا إشارة الفتح إن لم يكن الملف يحملها بعد.
        </li>
        <li>
          «تضمين النص والملفات المفتوحة» يضع النص والملفات المفتوحة في التنزيل دون تشفير: يستطيع أي شخص يملك الملف قراءتها.
        </li>
      </ul>

      <h2>هذا الجهاز</h2>
      <ul>
        <li>
          <b>قفل</b>: يزيل المفتاح من الذاكرة. <b>تسجيل الخروج</b>: ينهي هذه الجلسة؛ ويبقى المفتاح على هذا الجهاز. <b>تسجيل الخروج من جميع الأجهزة</b>:
          ينهي جلساتك على كل جهاز. <b>نسيان هذا الجهاز</b>: يحذف مفتاحك وكل ما خزّنه Vrelis في هذا المتصفح.
        </li>
        <li>يدوم تسجيل الدخول {l.sessionDays} يومًا.</li>
      </ul>

      <h2>ما يستطيع Vrelis رؤيته وما لا يستطيع</h2>
      <ul>
        <li>
          <b>لا يستطيع Vrelis قراءة</b> الكبسولة قبل وقتها، ولا الكبسولة الخاصة أبدًا (ليس لديه مفتاح لها)، ولا مفتاحك أو رمز PIN أو عبارتك.
        </li>
        <li>
          <b>يرى Vrelis</b>: اسمك وبريدك الإلكتروني (يرى المالك والمسؤولون البريد الإلكتروني)، ومن يرسل كبسولة وإلى من، ومتى أُرسلت، ومتى تُفتح الكبسولة
          العامة، والأحجام، وأن للكبسولة ملفات. بعد وقتها يستطيع كل عضو قراءة الكبسولة العامة، وبالتالي Vrelis أيضًا.
        </li>
        <li>
          لا تُخزَّن حلقة المفاتيح ونسخ مفتاحك إلا مشفّرة؛ ولا يستطيع Vrelis فتحها.
        </li>
        <li>
          يثبت التوقيع أي مفتاح كتب الكبسولة. الكبسولة الموسومة بـ «مفتاح مُلغى» وقّعها مفتاح أبلغ صاحبه لاحقًا عن سرقته. أما أن المفتاح يخص شخصًا بعينه فيعتمد على قائمة الأعضاء وعلى الاسم الذي وضعه المرسل في
          الكبسولة. تحقق من البصمات مع الأشخاص الذين تكتب إليهم كتابة خاصة.
        </li>
      </ul>

      <h2>الخطط والطوابع</h2>
      <ul>
        <li>
          <b>الطابع</b> الواحد يدفع ثمن تسليم واحد إلى مستلم واحد. نسختك الخاصة مجانية. تمنح الخطة المجانية طوابع شهرية: {l.freeStamps}، وتخزينًا: {l.freeStorage}، وملفات حتى {l.fileBytes} لكل رسالة؛ أما الخطة القديمة
          (طوابع شهرية: {l.legacyStamps}، و{l.planStorage}، و{l.planFileBytes}) فقد مُنحت لأوائل الأعضاء؛ وكانت Start ستمنح طوابع شهرية: {l.planStamps}. طوابع الشهر لا تُرحَّل، وتتجدد في
          اليوم الأول من الشهر التالي (UTC). تعرض <b>الفوترة</b> ما لديك، وما تبقى، وما هو مخطط.
        </li>
        <li>
          حاليًا لا تُفرض أي رسوم ولا يمكن شراء أي شيء: الخطط معاينة، ويفعّل المسؤول الطلب يدويًا. ما أرسلته يبقى قابلًا للقراءة لك وللأشخاص الذين أرسلته إليهم، مهما
          حدث لخطتك أو طوابعك.
        </li>
        <li>
          النسخة الخاصة بك اختيارية ومفعّلة افتراضيًا. إذا أوقفتها، فلن يستطيع قراءة تلك الرسالة إلا المستلم: لن تتمكن أنت من قراءتها، ولا حتى بعد فتحها.
        </li>
      </ul>

      <h2>حدود التجربة</h2>
      <ul>
        <li>
          {l.daily} عملية تسليم يوميًا كحد أقصى (نسختك الخاصة مجانية؛ والكبسولة إلى مجموعة تُحتسب واحدة لكل عضو). الرفع: {l.uploadDaily} و{l.unusedFiles} ملفات غير مستخدمة لكل 24 ساعة؛
          وتُحذف الملفات غير المستخدمة في كبسولة خلال 24 ساعة.
        </li>
        <li>سجّل الدخول عبر Google أو بكلمة مرور أو بمفتاح مرور. لا مدفوعات، ولا شبكة عامة: تبقى الكبسولات داخل التجربة.</li>
        <li>
          دعوة عنوان له حساب بالفعل ترسل إلى ذلك العضو طلب تواصل بدلًا من ذلك؛ وإذا قبل، يتابع كل منكما الآخر. تبدو
          شاشتك متطابقة في الحالتين، فلا يستطيع أحد استخدام الدعوات لمعرفة من هو عضو.
        </li>
      </ul>

      <h2>للمطورين</h2>
      <p>
        تستطيع الخدمات قراءة الكبسولات وإرسالها عبر <a href="/developers">Vrelis API</a>. تحصل الخدمة على حسابها الخاص عبر دعوة خدمة من
        مسؤول؛ وتبدو للجميع عضوًا عاديًا. تنشئ مفتاحها الخاص وتوقّع كل كبسولة بنفسها؛ تستطيع رموز API الخاصة بها القراءة والإرسال، لكنها
        لا تستطيع إدارة المفاتيح أو طرق تسجيل الدخول. يمكنك حظر خدمة أو إخفاؤها مثل أي شخص آخر.
      </p>

      <h2>ما نطلبه من المشاركين في التجربة</h2>
      <ol>
        <li>أرسل كبسولة عامة تُفتح بعد ساعة، وكبسولة خاصة إلى عضو آخر تُفتح بعد يوم.</li>
        <li>اقرأ الكبسولات العامة بعد فتحها، وحاول قراءة إحداها قبل ذلك: يجب ألا تتمكن من ذلك.</li>
        <li>أحدكم: أعدّ مفتاحك على جهاز ثانٍ واقرأ الوارد هناك.</li>
        <li>أحدكم: أخفِ عضوًا آخر وتحقق من أن كبسولاته تختفي من الخلاصة لديك وحدك.</li>
      </ol>

      <h2>المشكلات</h2>
      <p>
        استخدم <b>الإبلاغ عن مشكلة</b> أسفل كل صفحة، أو راسل {link}. اذكر ما فعلته، وما توقعته، وما حدث؛ ولقطة الشاشة تساعد.
        الخدمة وحدودها والمسؤوليات موصوفة في <a href="/terms">شروط الاستخدام</a>.
      </p>
    </>
  );
}
